/* eslint-disable no-console */
import React, { useMemo, useState, useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import InfoModal from '@shared/info-modal'
import { styled, useMediaQuery, Stack } from '@mui/material'
import { uniqueId } from 'lodash'
import { lockBodyScroll, unlockBodyScroll } from '@helpers/bodyscroll'
import { getSynchronyLink, getSDPEncryption } from '@helpers/finance'

/**
 * @param {string} url
 */
export const isSynchronyApplyLink = url => url?.includes('mysynchrony.com/eapply/eapply.action')

export const isSynchronyWithQueriesLink = url => url?.includes('&sitecode=')

export const isSynchronyUrlWithModalApplyParam = url => url?.toLowerCase?.()?.includes('modalapply=')

const hasPostBackRefIdParam = url => url?.toLowerCase?.()?.includes('postbackrefid=')

const StyledLink = styled('a')({
  cursor: 'pointer',
})

const StyledContainer = styled('iframe')({
  width: '100%',
  height: '100%',
  background: 'linear-gradient(-45deg, #E5E4E2, #C0C0C0, #D3D3D3, #DEDEDE)',
  backgroundSize: '400% 400%',
  animation: 'gradient 1s ease infinite',
  '@keyframes gradient': {
    '0%': {
      backgroundPosition: '0% 50%',
    },
    '50%': {
      backgroundPosition: '100% 50%',
    },
    '100%': {
      backgroundPosition: '0% 50%',
    },
  },
})

const StyledModal = styled(InfoModal)({
  '.modal-content': {
    height: '85vh !important',
    '& > div': {
      height: '100%',
      '& > div': {
        maxHeight: 'unset !important',
        overflowY: 'unset !important',
      },
    },
  },
})

const ApplyButton = ({
  action,
  asButton,
  asModal = true,
  category,
  children,
  handleOpen,
  handleClose,
  label,
  title,
  trackingData,
  url,
  value,
  ...rest
}) => {
  const id = useMemo(() => `syfClientID${uniqueId()}`, [])
  const [modalOpened, setModalOpened] = useState(false)
  const [synchronyLink, setSynchronyLink] = useState()
  const iframeRef = useRef(null)

  const isMobile = useMediaQuery(theme => theme?.breakpoints?.down('md'))
  const getPostbackTrackingString = () => {
    if (typeof window !== 'undefined') {
      const fingerPrintId = window?.localStorage?.getItem?.('fingerprint_id')
      const anonymousId = window?.analytics?.user?.()?.anonymousId?.()
      const userId = window?.analytics?.user?.()?.id?.()
      const trackingString = `&postBackRefId=${userId ?? fingerPrintId ?? anonymousId}`
      return trackingString
    }
    return ''
  }

  const postBackRefId = hasPostBackRefIdParam(url) ? '' : getPostbackTrackingString()
  const needsModalApplyParam = !isSynchronyUrlWithModalApplyParam(url)

  const formattedUrl = `${url}${needsModalApplyParam ? '&modalapply=true' : ''}${postBackRefId}`

  const isSynchronyPreScreenLink = url?.includes('preScreen=Y')

  const msgHandler = ({ data }) => {
    if (data?.action === 'syf-close-me' && data?.decision === 'UD') {
      onAfterClose()
    }
  }

  /**
   * @param {Event} e
   */
  const onApply = e => {
    e.preventDefault()
    window.syfClient({
      dApplyOpener: [id],
      dApplyUrl: `${url}${postBackRefId}`,
      addlistener: false,
    })
  }
  const sharedProps = {
    id,
    role: 'button',
    'gtm-category': category,
    'gtm-action': action,
    'gtm-value': value,
    'gtm-label': label,
    onClick: e => {
      if (trackingData) window.dataLayer.push(trackingData)

      if (asModal) {
        setModalOpened(true)
        if (isSynchronyPreScreenLink) {
          window.addEventListener('message', msgHandler)
        }
      } else {
        onApply(e)
      }

      handleOpen?.()
    },
    ...(!asButton && { target: '_blank', type: 'button' }),
  }

  useEffect(() => {
    let intervalId
    if (asModal && modalOpened) {
      intervalId = setInterval(() => {
        try {
          if (
            iframeRef?.current?.contentWindow?.location?.href?.startsWith('https://roomstogo.com') ||
            iframeRef?.current?.contentWindow?.location?.href?.startsWith('https://www.roomstogo.com')
          ) {
            setModalOpened(false)
          }
        } catch {}
      }, 1500)
    }
    return () => {
      clearInterval(intervalId)
    }
  }, [asModal, modalOpened])

  useEffect(() => {
    if (modalOpened) {
      getSDPEncryption()
        .then(sdpKey => {
          if (isSynchronyPreScreenLink) {
            setSynchronyLink(`${formattedUrl}${sdpKey}`)
          } else {
            setSynchronyLink(`${getSynchronyLink()}${sdpKey}${formattedUrl}`)
          }
        })
        .catch(() => setSynchronyLink(isSynchronyPreScreenLink ? formattedUrl : `${getSynchronyLink()}`))
    }
  }, [formattedUrl, isSynchronyPreScreenLink, modalOpened])

  const onAfterClose = () => {
    window.removeEventListener('message', msgHandler)
    if (isMobile) unlockBodyScroll()
    if (handleClose) handleClose()
  }

  // DISABLING SYNCHRONY LINKS MODAL TEMPORARILY
  if (asModal) {
    return (
      <>
        <Stack onClick={e => e.stopPropagation()}>
          <StyledModal
            label="Synchrony"
            shouldShowModal={modalOpened}
            closeModal={() => setModalOpened(false)}
            onAfterOpen={() => isMobile && lockBodyScroll()}
            onAfterClose={onAfterClose}
            shouldCloseOnOverlayClick={!isMobile}
            preventScroll
          >
            <StyledContainer
              ref={iframeRef}
              title={title}
              src={synchronyLink}
              referrerpolicy="no-referrer-when-downgrade"
              sandbox="allow-same-origin allow-scripts allow-popups allow-forms allow-popups-to-escape-sandbox allow-downloads"
            />
          </StyledModal>
        </Stack>
        <StyledLink variant="text" {...rest} {...sharedProps}>
          {children}
        </StyledLink>
      </>
    )
  }

  return (
    <StyledLink rel="nofollow" {...rest} {...sharedProps}>
      {children}
      {!asButton && <span className="hide508">(opens in new window)</span>}
    </StyledLink>
  )
}

ApplyButton.propTypes = {
  action: PropTypes.string,
  asButton: PropTypes.bool,
  asModal: PropTypes.bool,
  category: PropTypes.string,
  children: PropTypes.node,
  handleOpen: PropTypes.func,
  handleClose: PropTypes.func,
  label: PropTypes.string,
  title: PropTypes.string,
  trackingData: PropTypes.object,
  url: PropTypes.string,
  value: PropTypes.string,
}

export default ApplyButton
